import axios from "axios";
import { Capacitor } from "@capacitor/core";

function resolveApiBaseUrl() {
  if (Capacitor.isNativePlatform()) {
    return "https://getscribedash.com/api";
  }
  const configuredUrl = process.env.REACT_APP_BACKEND_URL?.trim();
  const fallbackUrl =
    typeof window !== "undefined" ? window.location.origin : "";

  const rootUrl = (configuredUrl || fallbackUrl).replace(/\/+$/, "");

  // Hostinger currently allows REACT_APP_BACKEND_URL to be either:
  // https://example.com or https://example.com/api
  return rootUrl.endsWith("/api") ? rootUrl : `${rootUrl}/api`;
}

export const API_BASE_URL = resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
});

let refreshPromise = null;

function refreshSessionOnce() {
  if (!refreshPromise) {
    refreshPromise = axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true, timeout: 30000 }
    ).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      (!originalRequest.url?.includes("/auth/") || originalRequest.url === "/auth/me")
    ) {
      originalRequest._retry = true;

      try {
        await refreshSessionOnce();

        return api(originalRequest);
      } catch (refreshError) {
        if (refreshError?.response?.status === 401 && typeof window !== "undefined") {
          window.dispatchEvent(new Event("auth:session-expired"));
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) {
    return "Something went wrong. Please try again.";
  }

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((entry) =>
        entry && typeof entry.msg === "string"
          ? entry.msg
          : JSON.stringify(entry)
      )
      .filter(Boolean)
      .join(" ");
  }

  if (detail && typeof detail.msg === "string") {
    return detail.msg;
  }

  return String(detail);
}
